import { useState } from "react";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { ToastContainer } from "../components/ui/toast";
import { Header } from "../components/Header";
import { SEO } from "../components/SEO";
import { QRCodeSVG } from "qrcode.react";
import { QrCode, ArrowRight, Download } from "lucide-react";
import logoIcon from "../assets/icone.png";
import { useLanguage } from "../hooks/useLanguage";

export function QRCodeGenerator() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<{ suffix: string; target_url: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type?: "success" | "error" | "info" }>>([]);
  const { t } = useLanguage();

  const addToast = (message: string, type: "success" | "error" | "info" = "info") => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const result = await api.createQRCode(url.trim());
      if (result.ok && result.data) {
        setGeneratedLink(result.data);
        addToast(t.qrCode.form.success, "success");
      } else {
        setError((result as any).error || t.qrCode.form.error);
      }
    } catch (err) {
      setError(t.qrCode.form.error);
    } finally {
      setLoading(false);
    }
  };

  const downloadQRCode = async () => {
    if (!generatedLink) return;

    try {
      const QRCode = await import('qrcode');
      const qrDataURL = await QRCode.toDataURL(api.getQRCodeUrl(generatedLink.suffix), {
        scale: 10,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      const downloadLink = document.createElement('a');
      downloadLink.download = `qr-code.png`;
      downloadLink.href = qrDataURL;
      downloadLink.click();
    } catch (error) {
      console.error('Error generating QR code:', error);
      addToast(t.qrCode.form.downloadError, 'error');
    }
  };

  return (
    <div className="min-h-screen bg-white selection:bg-indigo-100 selection:text-indigo-900 font-sans flex flex-col">
      <SEO
        title={t.qrCode.hero.title + " | Hub Links"}
        description="Gere QR Codes gratuitos para seus links. Compartilhe facilmente URLs através de códigos QR escaneáveis. Ferramenta gratuita e rápida."
        keywords="qr code, gerador qr code, qr code grátis, criar qr code, gerar qr code, qr code online"
        ogImage="https://hublinks.click/og-qr-code.jpg"
        ogType="website"
        canonicalUrl="https://hublinks.click/qr"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Gerador de QR Code - Hub Links",
          "url": "https://hublinks.click/qr",
          "description": "Gere QR Codes gratuitos para seus links de forma rápida e fácil",
          "applicationCategory": "UtilitiesApplication",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "BRL"
          }
        }}
      />
      {/* Hero Section with Gradient Background */}
      <div className="relative overflow-hidden flex-1">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-neutral-900/10 via-white to-white -z-10" />
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 -z-10 mix-blend-overlay"></div>

        <Header
          onLoginClick={() => window.location.href = '/login'}
          onRegisterClick={() => window.location.href = '/register'}
        />

        {/* Hero Content */}
        <main>
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0DAFFD]/10 border border-[#0DAFFD]/20 text-[#0DAFFD] text-xs font-semibold uppercase tracking-wider shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-700">
              <QrCode className="w-3 h-3" aria-hidden="true" />
              {t.qrCode.hero.badge}
            </div>

            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-slate-900 leading-[1.1] animate-in fade-in slide-in-from-bottom-6 duration-700 delay-100">
              {t.qrCode.hero.title} <br className="hidden md:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0DAFFD] via-[#0FD8FE] to-[#313131]">
                {t.qrCode.hero.titleHighlight}
              </span>
            </h1>

            <div className="max-w-md mx-auto animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label htmlFor="url" className="text-sm font-medium text-slate-700 text-left block">
                    {t.qrCode.form.urlLabel}
                  </label>
                  <input
                    id="url"
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder={t.qrCode.form.urlPlaceholder}
                    required
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:border-transparent placeholder:text-slate-400 bg-white/90 backdrop-blur-sm"
                  />
                </div>

                {error && (
                  <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm text-center font-medium">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading || !url.trim()}
                  className="w-full h-12 bg-gradient-to-r from-[#0DAFFD] to-[#0FD8FE] hover:from-[#0DAFFD] hover:to-[#0FD8FE] text-white rounded-xl font-semibold transition-all hover:shadow-lg hover:shadow-[#0DAFFD]/30 hover:-translate-y-0.5"
                >
                  {loading ? t.qrCode.hero.loading : t.qrCode.hero.cta}
                  {!loading && <ArrowRight className="ml-2 w-5 h-5" />}
                </Button>

                {generatedLink && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
                    <div className="w-full bg-white p-4 rounded-2xl border-2 border-slate-100 flex justify-center">
                      <QRCodeSVG
                        value={api.getQRCodeUrl(generatedLink.suffix)}
                        size={120}
                        level="M"
                        includeMargin={true}
                        className="w-auto h-auto"
                      />
                    </div>

                    <div className="text-center space-y-3">
                      <p className="text-xs text-slate-600 break-all">
                        {generatedLink.target_url}
                      </p>

                      <Button
                        onClick={downloadQRCode}
                        variant="ghost"
                        size="micro"
                        icon={<Download className="w-4 h-4" />}
                        className="text-slate-600 hover:text-indigo-600"
                      >
                        {t.qrCode.form.download}
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 pt-16 pb-8 mt-auto" role="contentinfo">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex items-center gap-2 font-bold text-lg text-slate-900">
              <img src={logoIcon} alt={t.footer.logoAlt} className="w-6 h-6 rounded-md" />
              Hub Links
            </div>
            <div className="text-sm text-slate-500">
              {t.footer.rights}
            </div>
            <nav className="flex gap-6" aria-label={t.footer.socialsAriaLabel}>
              <a href="#" className="text-slate-400 hover:text-indigo-600 transition-colors" aria-label="Twitter">Twitter</a>
              <a href="#" className="text-slate-400 hover:text-indigo-600 transition-colors" aria-label="Instagram">Instagram</a>
              <a href="#" className="text-slate-400 hover:text-indigo-600 transition-colors" aria-label="GitHub">GitHub</a>
            </nav>
          </div>
        </div>
      </footer>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
